import React, { useEffect, useState } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { adminService, type AdminDashboard, type FeedbackEntry } from '../../services/admin.service';
import { formatSAR } from '../../utils/formatters';
import {
  TrendingUp, Users, FileText, CheckCircle2, Star, MessageSquare,
  Award, ThumbsUp, BarChart2,
} from 'lucide-react';

const STAT_COLORS: Record<string, string> = {
  Draft:          '#9CA3AF',
  'In Progress':  '#9CA3AF',
  Sent:           '#3B82F6',
  Accepted:       '#16a34a',
  Rejected:       '#dc2626',
  Closed:         '#0d9488',
  Expired:        '#f59e0b',
};

function StarRating({ value, max = 5, size = 14 }: { value: number; max?: number; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {Array.from({ length: max }).map((_, i) => {
        const filled = i < Math.floor(value);
        const half = !filled && i < value;
        return (
          <Star
            key={i}
            size={size}
            fill={filled ? '#F59E0B' : half ? 'url(#half)' : 'none'}
            color={filled || half ? '#F59E0B' : '#D1D5DB'}
          />
        );
      })}
    </span>
  );
}

function RatingBar({ rating, count, max }: { rating: number; count: number; max: number }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  const starColor = rating >= 4 ? '#16a34a' : rating >= 3 ? '#F59E0B' : '#dc2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', width: 10, textAlign: 'right' }}>{rating}</span>
      <Star size={11} fill="#F59E0B" color="#F59E0B" />
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 3, background: starColor, width: `${pct}%`, transition: 'width .4s ease' }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-3)', width: 24, textAlign: 'right' }}>{count}</span>
    </div>
  );
}

function ScoreChip({ value }: { value: number | null }) {
  if (value === null) return <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No data</span>;
  const color = value >= 4 ? '#16a34a' : value >= 3 ? '#F59E0B' : '#dc2626';
  const bg = value >= 4 ? '#d1fae5' : value >= 3 ? '#fef3c7' : '#fee2e2';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', background: bg, color, borderRadius: 20, fontSize: 12.5, fontWeight: 700 }}>
      <Star size={11} fill={color} color={color} />
      {value.toFixed(1)}
    </span>
  );
}

function RecentFeedbackCard({ fb }: { fb: FeedbackEntry }) {
  return (
    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12 }}>
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        <div style={{
          width: 34, height: 34, borderRadius: '50%', background: 'var(--p-light)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700, color: 'var(--p)',
        }}>
          {fb.agentName.charAt(0).toUpperCase()}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>{fb.agentName}</span>
          <StarRating value={fb.rating} size={12} />
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: fb.comment ? 4 : 0 }}>
          {fb.resellerName} · {fb.offerNumber}
        </div>
        {fb.comment && (
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontStyle: 'italic', lineHeight: 1.45 }}>
            "{fb.comment}"
          </div>
        )}
      </div>
    </div>
  );
}

export const AdminDashboardPage: React.FC = () => {
  const [data, setData] = useState<AdminDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminService.getDashboard().then(setData).finally(() => setLoading(false));
  }, []);

  const fb = data?.feedback;

  // Distribution of ratings (1–5) for the histogram
  const ratingDist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  if (fb) {
    fb.recent.forEach((f) => { if (f.rating >= 1 && f.rating <= 5) ratingDist[f.rating]++; });
  }
  const maxDistCount = Math.max(...Object.values(ratingDist), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <AppNav title="Admin Dashboard" subtitle="Aggregate view across all resellers" />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        {loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="metric-tile">
                <div className="skeleton" style={{ height: 26, width: 60, marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 13, width: 80 }} />
              </div>
            ))}
          </div>
        )}

        {data && (
          <>
            {/* ── Top KPI tiles ──────────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '14px', marginBottom: '28px' }}>
              <StatCard icon={<FileText size={18} />} label="Total Offers" value={data.totalOffers} color="var(--p)" />
              <StatCard icon={<TrendingUp size={18} />} label="Total Value" value={formatSAR(data.totalValue)} color="var(--succ)" />
              <StatCard icon={<Users size={18} />} label="Resellers" value={data.resellerCount} color="#6366f1" />
              {Object.entries(data.counts).map(([status, count]) => (
                <StatCard key={status} icon={<CheckCircle2 size={18} />} label={status} value={count} color={STAT_COLORS[status] ?? '#374151'} />
              ))}
              {fb && fb.overallAvg !== null && (
                <StatCard
                  icon={<Star size={18} fill="#F59E0B" color="#F59E0B" />}
                  label="Avg. Feedback"
                  value={`${fb.overallAvg.toFixed(1)} / 5`}
                  color="#F59E0B"
                  sub={`${fb.totalResponses} response${fb.totalResponses !== 1 ? 's' : ''}`}
                />
              )}
            </div>

            {/* ── Reseller Breakdown ─────────────────────────────────────────── */}
            <div style={{ marginBottom: 28 }}>
              <div className="section-title" style={{ marginBottom: 12 }}>Reseller Breakdown</div>
              <div className="card" style={{ overflow: 'hidden' }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Reseller</th><th>Offers</th><th>Accepted</th><th>Sent</th><th>Closed</th>
                      <th>Total Value</th><th>Avg. Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.perReseller.map((r) => {
                      const rfb = fb?.perReseller.find((x) => x.id === r.id);
                      return (
                        <tr key={r.id}>
                          <td style={{ fontWeight: 600, color: 'var(--text-1)' }}>{r.name}</td>
                          <td>{r.offers}</td>
                          <td style={{ color: '#16a34a', fontWeight: 500 }}>{r.counts['Accepted'] ?? 0}</td>
                          <td style={{ color: '#3B82F6' }}>{r.counts['Sent'] ?? 0}</td>
                          <td>{r.counts['Closed'] ?? 0}</td>
                          <td style={{ fontWeight: 600, color: 'var(--p)' }}>{formatSAR(r.totalValue)}</td>
                          <td>
                            {rfb ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <ScoreChip value={rfb.avgRating} />
                                <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{rfb.count} review{rfb.count !== 1 ? 's' : ''}</span>
                              </div>
                            ) : <span style={{ fontSize: 12, color: 'var(--text-3)' }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Feedback section (only if there's feedback data) ───────────── */}
            {fb && fb.totalResponses > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 28 }}>

                {/* Rating distribution */}
                <div className="card">
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BarChart2 size={15} color="var(--p)" />
                    <span className="section-title" style={{ marginBottom: 0 }}>Rating Distribution</span>
                  </div>
                  <div className="card-body" style={{ padding: '16px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
                      <div>
                        <div style={{ fontSize: 40, fontWeight: 800, color: 'var(--text-1)', lineHeight: 1 }}>
                          {fb.overallAvg?.toFixed(1)}
                        </div>
                        <StarRating value={fb.overallAvg ?? 0} size={16} />
                        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                          {fb.totalResponses} response{fb.totalResponses !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        {[5, 4, 3, 2, 1].map((r) => (
                          <RatingBar key={r} rating={r} count={ratingDist[r]} max={maxDistCount} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top agents by score */}
                <div className="card">
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Award size={15} color="#F59E0B" />
                    <span className="section-title" style={{ marginBottom: 0 }}>Agent Scores</span>
                  </div>
                  <div className="card-body" style={{ padding: 0 }}>
                    {fb.perAgent.slice(0, 6).map((a, i) => (
                      <div key={a.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 18px',
                        borderBottom: i < Math.min(fb.perAgent.length, 6) - 1 ? '1px solid var(--border)' : 'none',
                        background: i === 0 ? 'rgba(245,158,11,.05)' : 'transparent',
                      }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: i === 0 ? '#FEF3C7' : 'var(--surface-alt)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700,
                          color: i === 0 ? '#92400E' : 'var(--text-2)',
                          flexShrink: 0,
                        }}>
                          {i === 0 ? <Award size={14} color="#F59E0B" /> : `#${i + 1}`}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {a.name}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{a.count} review{a.count !== 1 ? 's' : ''}</div>
                        </div>
                        <ScoreChip value={a.avgRating} />
                      </div>
                    ))}
                    {fb.perAgent.length === 0 && (
                      <div style={{ padding: '20px 18px', textAlign: 'center', fontSize: 13, color: 'var(--text-3)' }}>No agent data yet</div>
                    )}
                  </div>
                </div>

                {/* Reseller scores */}
                <div className="card">
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ThumbsUp size={15} color="var(--succ)" />
                    <span className="section-title" style={{ marginBottom: 0 }}>Reseller Scores</span>
                  </div>
                  <div className="card-body" style={{ padding: 0 }}>
                    {fb.perReseller.slice(0, 6).map((r, i) => (
                      <div key={r.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 18px',
                        borderBottom: i < Math.min(fb.perReseller.length, 6) - 1 ? '1px solid var(--border)' : 'none',
                      }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', background: 'var(--p-light)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, color: 'var(--p)', flexShrink: 0,
                        }}>
                          {r.name.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{r.count} review{r.count !== 1 ? 's' : ''}</div>
                        </div>
                        <ScoreChip value={r.avgRating} />
                      </div>
                    ))}
                    {fb.perReseller.length === 0 && (
                      <div style={{ padding: '20px 18px', textAlign: 'center', fontSize: 13, color: 'var(--text-3)' }}>No reseller data yet</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Recent Feedback ────────────────────────────────────────────── */}
            {fb && fb.recent.length > 0 && (
              <div>
                <div className="section-title" style={{ marginBottom: 12 }}>Recent Feedback</div>
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                    {fb.recent.map((f, i) => (
                      <div key={f.id} style={{
                        borderRight: i % 2 === 0 ? '1px solid var(--border)' : 'none',
                      }}>
                        <RecentFeedbackCard fb={f} />
                      </div>
                    ))}
                  </div>
                  {fb.totalResponses > fb.recent.length && (
                    <div style={{ padding: '12px 18px', textAlign: 'center', fontSize: 12.5, color: 'var(--text-3)', borderTop: '1px solid var(--border)', background: 'var(--surface-alt)' }}>
                      Showing latest {fb.recent.length} of {fb.totalResponses} responses
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Empty feedback state */}
            {fb && fb.totalResponses === 0 && (
              <div className="card" style={{ padding: '32px', textAlign: 'center' }}>
                <MessageSquare size={28} color="var(--text-3)" style={{ margin: '0 auto 10px' }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>No feedback yet</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Agent ratings will appear here once customers submit feedback on accepted offers.</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
  sub?: string;
}> = ({ icon, label, value, color, sub }) => (
  <div className="metric-tile" style={{ borderTop: `3px solid ${color}` }}>
    <div style={{ color, opacity: 0.75, marginBottom: 10 }}>{icon}</div>
    <div style={{ fontSize: '22px', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '4px' }}>{label}</div>
    {sub && <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
  </div>
);
